import DefaultScreen from "@components/Global/DefaultScreenView";
import React, { useRef, useCallback } from "react";
import { FlatList, RefreshControl, View, Text } from "react-native";
import { useQuery } from "@tanstack/react-query";
import { Spinner } from "tamagui";
import { getCallFlows } from "@api/api";
import globalStyles from "@stylesheet";
import { Entypo, MaterialIcons } from "@expo/vector-icons";
import FlowItem from "./Components/FlowItem";
import AddCallFlowModal from "./Components/AddCallFlowModal";
import { themeSelector } from "@reduxLocal/selectors/themeSelector";

const LazyRobot = React.lazy(() => import("@components/RobotHelper/SwipeableRobot"));

const CallFlows = () => {
  const robotRef = useRef(null);
  const addCallRef = useRef(null);
  const theme = themeSelector();
  
  const { isFetching, refetch, isLoading, data } = useQuery({
    queryKey: ["callFlows"], 
    queryFn: async () => {
      const res = await getCallFlows("");
      return res;
    }
  });

  function handleShowHelp() {
    robotRef.current.showHelp();
  }

  function addCallFlow() {
    addCallRef.current.openModal();
  }

  // Modern empty state component
  const EmptyStateComponent = useCallback(() => (
    <View style={[
      globalStyles.modernCard,
      theme ? globalStyles.modernBgLight : globalStyles.modernBgDark,
      { alignItems: 'center', padding: 32, marginHorizontal: 16 }
    ]}>
      <View style={{ alignItems: 'center', gap: 16 }}>
        <MaterialIcons 
          name="workspaces" 
          size={80} 
          color={theme ? '#6b7280' : '#9ca3af'} 
        />
        <Text style={{ 
          color: theme ? '#1e293b' : '#f1f5f9', 
          fontSize: 20,
          fontWeight: '700',
          textAlign: 'center'
        }}>
          No flows yet
        </Text>
        <Text style={{ 
          color: theme ? '#64748b' : '#94a3b8', 
          fontSize: 15,
          textAlign: 'center',
          lineHeight: 22
        }}>
          Create your first call flow to get started with automated workflows and voice interactions
        </Text>
      </View>
    </View>
  ), [theme]);

  // Modern loading state component
  const LoadingStateComponent = useCallback(() => (
    <View style={[
      globalStyles.modernCard,
      theme ? globalStyles.modernBgLight : globalStyles.modernBgDark,
      { alignItems: 'center', padding: 32, marginHorizontal: 16 }
    ]}>
      <View style={{ alignItems: 'center', gap: 20 }}>
        <Spinner size="large" color="$blue10" />
        <Text style={{
          color: theme ? '#1e293b' : '#f1f5f9',
          fontSize: 16,
          fontWeight: '600'
        }}>
          Loading your flows...
        </Text>
      </View>
    </View>
  ), [theme]);

  return (
    <DefaultScreen 
      scrollDisabled 
      searchBar={{
        placeHolder: "Search through your call flows",
        customIcons: [
          {
            function: handleShowHelp,
            icon: <Entypo name="help" size={18} color={"white"} />,
            functionName: 'help'
          },
          {
            function: addCallFlow,
            icon: <Entypo name="plus" size={18} color={"white"} />,
            functionName: 'addCallFlow'
          }
        ]
      }} 
      searchBarStyle={{ top: -40 }} 
      backButton 
      containerStyle={{ paddingBottom: 100 }}
    >
      <AddCallFlowModal ref={addCallRef}/>
      
      {isLoading ? (
        <View style={{ paddingTop: 100 }}>
          <LoadingStateComponent />
        </View>
      ) : (
        <FlatList
          data={data}
          contentContainerStyle={{ paddingBottom: 150, paddingTop: 8 }}
          showsVerticalScrollIndicator={false}
          refreshing={isFetching}
          refreshControl={
            <RefreshControl
              refreshing={isFetching}
              onRefresh={refetch}
              tintColor={theme ? '#64748b' : '#94a3b8'}
            />
          }
          ListEmptyComponent={
            <View style={{ paddingTop: 100 }}>
              <EmptyStateComponent />
            </View>
          }
          renderItem={({ item, index }) => (
            <FlowItem item={item} index={index} />
          )}
          keyExtractor={(item, index) => item._id || `flow-${index}`}
        />
      )}
      
      <View style={{ position: 'absolute', bottom: 80, width: '100%' }}>
        <LazyRobot ref={robotRef} textHelp={[
          "Create a new call flow by pressing the + button",
          "Edit an existing flow by tapping on it",
          "Flows show stages, actions, and usage statistics",
          "Use the search bar to find specific flows quickly"
        ]} />
      </View>
    </DefaultScreen>
  )
}
export default CallFlows;